/**
 * Carrusel de imágenes (keen-slider) para el layout `images_slider` del
 * page-section. Markup: .hg-images-slider > .hg-images-slider__viewport >
 * .keen-slider.hg-images-slider__slider > .keen-slider__slide, nav, dots.
 */

import KeenSlider from 'keen-slider'
import type { KeenSliderInstance } from 'keen-slider'
import 'keen-slider/keen-slider.min.css'

export default class ImagesSlider {
	private wrapper: HTMLElement
	private sliderRoot: HTMLElement | null = null
	private slider: KeenSliderInstance | null = null
	private prevBtn: HTMLButtonElement | null = null
	private nextBtn: HTMLButtonElement | null = null
	private dotButtons: HTMLButtonElement[] = []
	private boundPrev: (() => void) | null = null
	private boundNext: (() => void) | null = null
	private boundDots: Array<() => void> = []

	constructor(wrapper: HTMLElement) {
		this.wrapper = wrapper
	}

	init() {
		this.sliderRoot = this.wrapper.querySelector<HTMLElement>('.hg-images-slider__slider')
		if (!this.sliderRoot) return

		const slideCount = this.sliderRoot.querySelectorAll('.keen-slider__slide').length
		if (slideCount < 2) return

		const loop = this.wrapper.dataset.loop !== '0'

		this.prevBtn = this.wrapper.querySelector<HTMLButtonElement>('.hg-images-slider__nav--prev')
		this.nextBtn = this.wrapper.querySelector<HTMLButtonElement>('.hg-images-slider__nav--next')
		this.dotButtons = Array.from(
			this.wrapper.querySelectorAll<HTMLButtonElement>('.hg-images-slider__dot')
		)

		this.slider = new KeenSlider(this.sliderRoot, {
			loop,
			mode: 'snap',
			rubberband: true,
			slideChanged: () => this.syncUi(),
			created: () => this.syncUi(),
		})

		this.boundPrev = () => {
			this.slider?.prev()
		}
		this.boundNext = () => {
			this.slider?.next()
		}
		this.prevBtn?.addEventListener('click', this.boundPrev)
		this.nextBtn?.addEventListener('click', this.boundNext)

		this.boundDots = this.dotButtons.map((btn, idx) => () => {
			this.slider?.moveToIdx(idx, false)
		})
		this.dotButtons.forEach((btn, idx) => {
			btn.addEventListener('click', this.boundDots[idx]!)
		})
	}

	cleanup() {
		if (this.boundPrev && this.prevBtn) {
			this.prevBtn.removeEventListener('click', this.boundPrev)
		}
		if (this.boundNext && this.nextBtn) {
			this.nextBtn.removeEventListener('click', this.boundNext)
		}
		this.dotButtons.forEach((btn, idx) => {
			const fn = this.boundDots[idx]
			if (fn) btn.removeEventListener('click', fn)
		})
		this.boundPrev = null
		this.boundNext = null
		this.boundDots = []

		this.slider?.destroy()
		this.slider = null
	}

	private syncUi = () => {
		if (!this.slider?.track?.details) return
		const rel = this.slider.track.details.rel
		this.dotButtons.forEach((dot, i) => {
			const on = i === rel
			dot.classList.toggle('is-active', on)
			dot.setAttribute('aria-current', on ? 'true' : 'false')
		})
	}
}
